import { Component, OnInit } from '@angular/core';
import { FooterComponent } from '../../shared/footer/footer.component';
import { HeaderComponent } from '../../shared/header/header.component';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { StepperComponent } from '../../shared/stepper/stepper.component';
import { CompanyService } from '../../shared/services/company.service';
import { UrlReferenceService } from '../../shared/services/url-reference.service';
import { AuthService } from '../../shared/services/auth.service';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { filter, take } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-onboarding',
  standalone: true,
  imports: [
    CommonModule,
    FooterComponent,
    HeaderComponent,
    HttpClientModule,
    ReactiveFormsModule,
    StepperComponent,
    TranslateModule,
  ],
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss'],
})
export class OnboardingComponent implements OnInit {
  activeStep: number = 2;
  onboardingForm!: FormGroup;
  isLoading = false;
  currentUserId: string | undefined;
  currentUserRoles: string[] = [];
  userCompanies: any[] = [];
  selectedCompany: any = null;
  isComingFromDashboard = false;
  savedPolicyStartDate: string | null = null;
  userEmail: string = '';

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    private companyService: CompanyService,
    private urlRef: UrlReferenceService,
    private authService: AuthService,
    private toastService: ToastrService
  ) {}

  ngOnInit() {
    console.log('=== ONBOARDING COMPONENT INIT ===');
    console.log(
      'localStorage from-get-quote:',
      localStorage.getItem('from-get-quote')
    );
    console.log('localStorage company:', localStorage.getItem('company'));
    console.log(
      'localStorage savedPolicyStartDate:',
      localStorage.getItem('savedPolicyStartDate')
    );

    this.initializeForm();
    this.checkIfComingFromDashboard();
    this.loadSavedPolicyStartDate();

    // Check if coming from get-quote flow - bypass authentication completely
    const fromGetQuote = localStorage.getItem('from-get-quote');
    if (fromGetQuote === 'true') {
      console.log(
        'Coming from get-quote flow - loading company data directly without authentication'
      );
      // Directly load company data without any authentication checks
      this.getCompanyData();
    } else {
      console.log('Regular flow - checking authentication');
      this.checkAuthentication();
    }
  }

  private checkIfComingFromDashboard(): void {
    // Check if we have route state or query params indicating we're coming from dashboard
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state?.['fromDashboard']) {
      this.isComingFromDashboard = true;

      // If we have a selected company from the navigation state, use it
      const selectedCompany = navigation.extras.state?.['selectedCompany'];
      if (selectedCompany) {
        console.log('Selected company from navigation state:', selectedCompany);
        this.selectedCompany = selectedCompany;
        // Set company data in service for consistency
        this.companyService.setCompanyData(selectedCompany);
        this.companyService.setCompanyDetails(selectedCompany);
      }
    }
  }

  private loadSavedPolicyStartDate(): void {
    // Load saved policy start date from localStorage
    const savedDate = localStorage.getItem('savedPolicyStartDate');
    if (savedDate) {
      this.savedPolicyStartDate = savedDate;
      console.log('Loaded saved policy start date:', savedDate);

      // Don't set it in the form here - wait for loadCompanyData to handle it properly
      console.log(
        'Saved policy start date will be applied after company data loads'
      );
    }
  }

  private clearSavedPolicyStartDate(): void {
    this.savedPolicyStartDate = null;
    localStorage.removeItem('savedPolicyStartDate');
    console.log('Saved policy start date cleared');
  }

  private checkAuthentication(): void {
    console.log('=== CHECKING AUTHENTICATION ===');
    this.authService.isAuthenticated$
      .pipe(
        filter((authenticated: any) => authenticated),
        take(1)
      )
      .subscribe({
        next: () => {
          console.log('Authentication successful, loading user info');
          this.loadCurrentUserInfo().then(() => {
            if (this.isComingFromDashboard && this.currentUserId) {
              if (this.selectedCompany) {
                // If we already have a selected company from navigation, load its data
                console.log('Loading data for selected company from dashboard');
                this.loadCompanyDataFromSelected();
              } else {
                // Otherwise, load user companies to select from
                this.loadUserCompanies();
              }
            } else {
              // Standard flow - get company data from service
              console.log('Standard flow - getting company data');
              this.getCompanyData();
            }
          });
        },
        error: (error) => {
          console.error('Authentication failed:', error);
          console.log('Redirecting to login due to auth failure');
          this.router.navigate(['/login']);
        },
      });
  }

  getCompanyData(): void {
    // Check if we're coming from the landing page (get quote flow)
    const fromGetQuote = localStorage.getItem('from-get-quote');

    console.log('getCompanyData - fromGetQuote:', fromGetQuote);

    // First try to get from company details (localStorage), then from company data (memory)
    let company = this.companyService.getCompanyDetails();
    if (!company) {
      company = this.companyService.getCompanyData();
      // If we got company data from memory, also save it to localStorage for consistency
      if (company && company.id) {
        console.log('Saving company data to localStorage for consistency');
        this.companyService.setCompanyDetails(company);
      }
    }

    // Fallback to localStorage 'company' key if service doesn't have data
    if (!company) {
      const companyFromStorage = localStorage.getItem('company');
      if (companyFromStorage) {
        try {
          company = JSON.parse(companyFromStorage);
          console.log(
            'Using company data from localStorage fallback:',
            company
          );
          // Set it back in the service for consistency
          if (company) {
            this.companyService.setCompanyData(company);
            this.companyService.setCompanyDetails(company);
          }
        } catch (error) {
          console.error('Error parsing company data from localStorage:', error);
        }
      }
    }

    // Additional fallback: try to get company data response and merge with saved policy date
    if (!company && fromGetQuote === 'true') {
      const companyDataResponse = this.companyService.getCompanyDataResponse();
      const savedPolicyDate = localStorage.getItem('savedPolicyStartDate');

      if (companyDataResponse) {
        console.log(
          'Using company data response as fallback:',
          companyDataResponse
        );
        company = {
          ...companyDataResponse,
          policyStartDate:
            savedPolicyDate || companyDataResponse.policyStartDate,
        };

        // Set the reconstructed company data in service
        this.companyService.setCompanyData(company);
        if (company) {
          this.companyService.setCompanyDetails(company);
        }
      }
    }

    let companyId = company?.id;

    console.log('Company ID from service:', companyId);
    console.log('Company object:', company);
    console.log('Coming from get quote:', fromGetQuote);

    if (companyId && fromGetQuote === 'true') {
      // When coming from landing page, use the new getCompanyById API
      console.log(
        'Fetching company details by ID from landing page flow:',
        companyId
      );
      this.companyService.getCompanyById(companyId).subscribe({
        next: (fetchedCompany) => {
          console.log(
            'Fetched company details from getCompanyById API:',
            fetchedCompany
          );

          // Merge with existing data to preserve form inputs
          const mergedData = {
            ...fetchedCompany,
            crNumber: (company as any)?.crNumber || fetchedCompany.crNumber,
            policyStartDate:
              (company as any)?.policyStartDate ||
              fetchedCompany.policyStartDate,
            email: (company as any)?.email || (fetchedCompany as any).email,
            phoneNumber:
              (company as any)?.phoneNumber || fetchedCompany.phoneNumber,
          };

          console.log('Merged company data to be used:', mergedData);
          this.userEmail = mergedData.email || '';
          this.companyService.setCompanyData(mergedData);
          this.companyService.setCompanyDetails(mergedData);
          this.loadCompanyData();
        },
        error: (error) => {
          console.error('Error fetching company by ID:', error);
          // If API call fails, use the existing company data
          this.loadCompanyData();
        },
      });
    } else if (companyId) {
      // For dashboard flow, use the existing getCompanyByCompanyId method
      console.log('Fetching company by ID (dashboard flow):', companyId);
      this.companyService
        .getCompanyByCompanyId(companyId.toString())
        .subscribe({
          next: (fetchedCompany) => {
            console.log('Fetched company from API:', fetchedCompany);
            this.companyService.setCompanyData(fetchedCompany);
            this.companyService.setCompanyDetails(fetchedCompany);
            this.loadCompanyData();
          },
          error: (error) => {
            console.error('Error fetching company:', error);
            // If API call fails, use the existing company data
            this.loadCompanyData();
          },
        });
    } else if (company) {
      // If we have company data but no ID, use the data directly
      console.log('Using company data directly without API call');
      this.loadCompanyData();
    } else {
      console.log('No company data found');
      // Final fallback - try to load directly
      this.loadCompanyData();
    }
  }

  private initializeForm(): void {
    this.onboardingForm = this.fb.group({
      crNumber: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
      companyNameArabic: ['', Validators.required],
      policyStartDate: ['', Validators.required],
      contactPhone: [
        '',
        [Validators.required, Validators.pattern(/^\+?\d{10,15}$/)],
      ],
      name: ['', Validators.required],
      numberOfEmployees: ['', [Validators.required, Validators.min(1)]],
      sponsorID: [''], // Optional field
      entity700Number: [''], // Optional field
    });
  }

  private loadCompanyData(): void {
    console.log('=== LOADING COMPANY DATA ===');

    let company = this.companyService.getCompanyData();
    console.log('Company from service getCompanyData():', company);

    // Fallback to localStorage if company service doesn't have data
    if (!company) {
      const companyFromStorage = localStorage.getItem('company');
      console.log('Raw company data from localStorage:', companyFromStorage);
      if (companyFromStorage) {
        try {
          company = JSON.parse(companyFromStorage);
          console.log(
            'Parsed company data from localStorage fallback:',
            company
          );
          // Set it back in the service for consistency
          this.companyService.setCompanyData(company);
        } catch (error) {
          console.error('Error parsing company data from localStorage:', error);
        }
      }
    }

    const companyDataResponse = this.companyService.getCompanyDataResponse();
    console.log('Company data response from service:', companyDataResponse);

    console.log('Final company data to be used:', company);
    console.log(
      'Coming from get quote (localStorage):',
      localStorage.getItem('from-get-quote')
    );

    if (company) {
      // Use saved policy start date if available, otherwise use company's policy start date
      const savedDate =
        this.savedPolicyStartDate ||
        localStorage.getItem('savedPolicyStartDate');
      const companyPolicyDate = company.policyStartDate
        ? new Date(company.policyStartDate).toISOString().substring(0, 10)
        : '';

      // Prioritize saved date over company date
      const formattedDate = savedDate || companyPolicyDate;

      // localStorage.setItem('sponsorId', company.crNumber || '');
      this.companyService.setCrNumber(company.crNumber || '');

      const formData = {
        crNumber: company.crNumber || '',
        companyNameArabic:
          company.companyNameArabic ||
          companyDataResponse?.companyNameArabic ||
          '',
        name: company.name || companyDataResponse?.companyName || '', // Keep empty if not available
        policyStartDate: formattedDate,
        contactPhone: company.phoneNumber || company.contactPhone || '', // Keep empty if not available
        numberOfEmployees:
          company.numberOfEmployees ||
          companyDataResponse?.numberOfEmployees ||
          '',
        sponsorID: company.sponsorID || '',
        entity700Number: company.entity700Number || '',
      };

      console.log('Form data to be patched:', formData);
      console.log('Policy start date being used:', formattedDate);
      console.log(
        'Source: saved date =',
        savedDate,
        ', company date =',
        companyPolicyDate
      );
      console.log('SponsorID from company data:', company.sponsorID);
      console.log('Entity700Number from company data:', company.entity700Number);

      // Don't reset the form - just patch the values
      console.log('Form before patch:', this.onboardingForm.value);

      // Patch the form with the prepared data
      this.onboardingForm.patchValue(formData);

      console.log('Form values after patch:', this.onboardingForm.value);
      console.log(
        'Policy start date control value:',
        this.onboardingForm.get('policyStartDate')?.value
      );

      // Force change detection
      setTimeout(() => {
        console.log('After timeout - Form values:', this.onboardingForm.value);
        console.log(
          'After timeout - Policy start date control:',
          this.onboardingForm.get('policyStartDate')?.value
        );

        // Disable only specific fields that have data and should not be editable
        if (company.crNumber) {
          this.onboardingForm.get('crNumber')?.disable();
          console.log('CR Number disabled (has data)');
        }

        if (company.companyNameArabic) {
          this.onboardingForm.get('companyNameArabic')?.disable();
          console.log('Company Name Arabic disabled (has data)');
        }

        // Only disable policy start date if it came from company data (not saved date)
        if (company.policyStartDate && !savedDate) {
          this.onboardingForm.get('policyStartDate')?.disable();
          console.log('Policy start date disabled (from company data)');
        } else if (savedDate) {
          // Keep policy start date enabled if it's from saved data to allow editing
          this.onboardingForm.get('policyStartDate')?.enable();
          console.log('Policy start date kept enabled (from saved data)');
        }

        // Keep name and contactPhone enabled even if they have data, so user can edit/complete them
        this.onboardingForm.get('name')?.enable();
        this.onboardingForm.get('contactPhone')?.enable();
        this.onboardingForm.get('numberOfEmployees')?.enable();

        console.log('Form enabled/disabled states:');
        Object.keys(this.onboardingForm.controls).forEach((key) => {
          const control = this.onboardingForm.get(key);
          console.log(
            `${key}: ${control?.disabled ? 'DISABLED' : 'ENABLED'} (value: "${
              control?.value
            }")`
          );
        });
      }, 100);
    } else {
      console.log('No company data found in service or localStorage');
    }
  }

  private async loadCurrentUserInfo(): Promise<void> {
    try {
      console.log('=== LOADING USER INFO ===');
      this.isLoading = true;
      await this.authService.getToken();

      const profile = await this.authService.getUserInfo();
      this.currentUserId = this.authService.keycloakInstance.subject!;
      this.currentUserRoles = this.authService.getRoles();

      console.log('User loaded successfully:', {
        id: this.currentUserId,
        roles: this.currentUserRoles,
      });
    } catch (error) {
      console.error('User info error:', error);

      // For get-quote flow, don't redirect to login - just continue with the flow
      const fromGetQuote = localStorage.getItem('from-get-quote');
      if (fromGetQuote === 'true') {
        console.log(
          'Get-quote flow detected, continuing without user authentication'
        );
        return; // Don't redirect, just continue
      }

      this.toastService.error('Please login to continue');
      console.log('Redirecting to login due to user info error');
      this.router.navigate(['/login']);
    } finally {
      this.isLoading = false;
    }
  }

  private loadUserCompanies(): void {
    if (!this.currentUserId) {
      console.error('No user ID available to load companies');
      return;
    }

    this.isLoading = true;
    this.companyService
      .getCompaniesByUserIdOrUserEmail(this.currentUserId, this.userEmail)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (companies) => {
          this.userCompanies = companies;
          console.log('User companies loaded:', companies);

          if (companies && companies.length > 0) {
            // If there's only one company, auto-select it
            // If multiple companies, you might want to show a selection UI
            this.selectedCompany = companies[0];
            this.loadCompanyDataFromSelected();
          } else {
            // No companies found, show empty form
            console.log('No companies found for user');
          }
        },
        error: (error) => {
          console.error('Error loading user companies:', error);
          this.toastService.error('Failed to load company information');
          // Fallback to the original company data method
          this.getCompanyData();
        },
      });
  }

  private loadCompanyDataFromSelected(): void {
    if (!this.selectedCompany) return;

    const company = this.selectedCompany;
    console.log('Loading company data from selected company:', company);

    // Set company data in service for consistency
    this.companyService.setCompanyData(company);
    this.companyService.setCompanyDetails(company);

    // Use saved policy start date if available, otherwise use company's policy start date
    const savedDate =
      this.savedPolicyStartDate || localStorage.getItem('savedPolicyStartDate');
    const formattedDate =
      savedDate ||
      (company.policyStartDate
        ? new Date(company.policyStartDate).toISOString().substring(0, 10)
        : '');

    this.companyService.setCrNumber(company.crNumber || '');
    this.onboardingForm.patchValue({
      crNumber: company.crNumber || '',
      companyNameArabic: company.companyNameArabic || company.nameAr || '',
      name: company.name || company.companyName || company.nameEn || '',
      policyStartDate: formattedDate,
      contactPhone: company.contactPhone || company.phoneNumber || '',
      numberOfEmployees: company.numberOfEmployees || '',
      sponsorID: company.sponsorID || '',
      entity700Number: company.entity700Number || '',
    });

    console.log('Form values after patch:', this.onboardingForm.value);
    console.log('Policy start date used:', formattedDate);
    console.log(
      'Source: saved date =',
      savedDate,
      ', company date =',
      company.policyStartDate
    );
    console.log('Policy start date set to:', formattedDate);
    console.log('SponsorID from selected company:', company.sponsorID);
    console.log('Entity700Number from selected company:', company.entity700Number);

    // Enable all fields when coming from dashboard for editing
    Object.keys(this.onboardingForm.controls).forEach((key) => {
      this.onboardingForm.get(key)?.enable();
    });
  }

  handleBack(): void {
    window.history.back();
  }

  selectCompany(company: any): void {
    this.selectedCompany = company;
    this.loadCompanyDataFromSelected();
  }

  onContinue(): void {
    if (this.onboardingForm.invalid) {
      this.onboardingForm.markAllAsTouched();
      this.toastService.error('Please fill all required fields correctly');
      return;
    }

    this.isLoading = true;

    // Save the policy start date to local storage and component property
    const policyStartDate = this.onboardingForm.get('policyStartDate')?.value;
    if (policyStartDate) {
      this.savedPolicyStartDate = policyStartDate;
      localStorage.setItem('savedPolicyStartDate', policyStartDate);
      console.log('Policy start date saved:', policyStartDate);
    }

    // Determine if we're updating an existing company
    const isUpdating = this.isComingFromDashboard && this.selectedCompany?.id;

    // Combine the form data with the initial company data
    const formData = {
      ...this.onboardingForm.getRawValue(), // gets values including disabled fields
    };

    console.log('Form submission - Is updating:', isUpdating);
    console.log('Form data:', formData);
    console.log('Selected company ID:', this.selectedCompany?.id);

    let saveObservable: Observable<any>;

    // Check if this is a get-quote flow (no authentication required)
    const fromGetQuote = localStorage.getItem('from-get-quote');

    if (isUpdating && this.currentUserId) {
      // Use update method for existing companies
      console.log('Updating company with ID:', this.selectedCompany.id);
      saveObservable = this.companyService.updateCompany(
        this.selectedCompany.id,
        formData,
        this.currentUserId
      );
    } else if (this.currentUserId) {
      // Create new company
      console.log('Creating new company');
      saveObservable = this.companyService.createCompany(
        formData,
        this.currentUserId
      );
    } else {
      // Fallback to direct HTTP call - always use this for get-quote flow
      console.log('Fallback to direct HTTP call');
      const saveUrl =
        this.urlRef.saveCompanyUrl +
        (this.currentUserId ? `?userId=${this.currentUserId}` : '') +
        (!this.currentUserId && this.userEmail
          ? `?userEmail=${this.userEmail}`
          : '');
      saveObservable = this.http.post(saveUrl, {
        ...formData,
        ...(isUpdating && { id: this.selectedCompany.id }),
      });
    }

    saveObservable.pipe(finalize(() => (this.isLoading = false))).subscribe({
      next: (res: any) => {
        console.log('Save response:', res);
        this.companyService.setCompanyData(res || formData);

        // Update the selected company with the response data
        if (res && res.id) {
          localStorage.setItem('companyId', res.id.toString());
          this.companyService.setCompanyDetails(res);
        }

        this.toastService.success(
          isUpdating
            ? 'Company updated successfully'
            : 'Company saved successfully'
        );

        // Clear saved policy start date after successful submission
        this.clearSavedPolicyStartDate();

        // Navigation logic based on user context
        const fromGetQuote = localStorage.getItem('from-get-quote');

        if (fromGetQuote === 'true') {
          // Get-quote flow - proceed to next step
          console.log('Get-quote flow - proceeding to import employees');
          this.router.navigate(['/import-employees']);
        } else if (this.currentUserRoles.includes('admin')) {
          this.router.navigate(['/admin/dashboard']);
        } else if (this.isComingFromDashboard) {
          // Return to dashboard after updating
          this.router.navigate(['/dashboard/home']);
        } else {
          this.router.navigate(['/import-employees']);
        }
      },
      error: (err: any) => {
        console.error('Submission error:', err);
        this.toastService.error(
          err.error?.message ||
            `Failed to ${isUpdating ? 'update' : 'save'} company`
        );
      },
    });
  }
}
